/*
  A location several people count at once, and whose count each line is.

  Written 5 September 2026 for job 10, to the approved desktop drawing
  `docs/design/count-entry/desktop.png` (deleted 12 September 2026; kept in the tag before-phase-3-cleanup), which carries the gold band across the
  top ("Maria Delgado is counting this location at the same time") and the small
  chip on a line somebody else counted ("MARIA D.").

  **Only a regional or branch office allows it**, and there are two in the whole
  company today. Everywhere else, one person holds the sheet and the app blocks a
  second person for thirty minutes, which is the same window used here.

  **What the app did before: nothing.** A shared location showed a "LIVE" chip on
  a phone, nowhere on a desk, and never named anybody. Two people could count the
  same shelf, watch each other's numbers appear in their own boxes, and have no
  way of knowing that was another person rather than the screen misbehaving.
*/

// The window that counts as "at the same time". Thirty minutes, which is the
// same figure the session lock uses elsewhere on this screen for the same
// question, rather than a second number meaning nearly the same thing.
export const SAME_TIME_MINUTES = 30;

/*
  A person's name as the chip on a line shows it. Maria Delgado becomes Maria D.

  **A chip carries a name, not a sentence.** The full name is on the band at the
  top where there is room for it; on a line it sits in a column that is already
  narrow, and rule 30 says a label that wraps is a fault.
*/
export function shortName(full) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return parts[0] + ' ' + parts[parts.length - 1][0] + '.';
}

/*
  Who else has saved a line on this sheet in the last half hour.

  Read off the lines themselves: every line carries who last changed it and
  when, and the live feed updates both as other people save. That is a fact the
  sheet already holds, rather than a second thing to keep in step with it.
*/
export function otherCounters(items, myId, now = Date.now(), minutes = SAME_TIME_MINUTES) {
  const cutoff = now - minutes * 60 * 1000;
  const seen = new Map();
  for (const i of items || []) {
    // `last_updated_by` is written from 18 September 2026; lines saved before
    // then carry only `updated_by`.
    const who = i?.last_updated_by || i?.updated_by;
    if (!who || who === myId) continue;
    const at = i?.last_updated_at ? Date.parse(i.last_updated_at) : NaN;
    if (!(at >= cutoff)) continue;
    const prev = seen.get(who);
    if (prev) { prev.lines += 1; if (at > prev.at) prev.at = at; }
    else seen.set(who, { id: who, name: i.last_updated_name || 'Somebody else', lines: 1, at });
  }
  return [...seen.values()].sort((a, b) => b.at - a.at);
}

/*
  The sentence on the band, in the words of the drawing.

  **It says what happens to the work, not what the feature is called.** The two
  questions a person actually has when somebody else is in the same sheet are
  whether they are about to overwrite each other and whether they need to
  refresh anything. The answer to both is on the band.
*/
export function sharedSentence(others) {
  const list = others || [];
  if (list.length === 0) {
    return 'Several people can count this location at once. Any line somebody else saves appears here as they save it.';
  }
  if (list.length === 1) {
    return list[0].name + ' is counting this location at the same time. Their lines appear here as they save them, and a line you are typing into is left alone.';
  }
  const names = list.map(o => o.name);
  const last  = names.pop();
  return names.join(', ') + ' and ' + last + ' are counting this location at the same time. Their lines appear here as they save them, and a line you are typing into is left alone.';
}

/*
  Whose count one line is, for the chip. Empty for my own lines and for a line
  nobody has counted, because a chip on every row is a chip that says nothing.
*/
export function lineOwner(item, myId) {
  const who = item?.last_updated_by || item?.updated_by;
  if (!who || who === myId) return '';
  return shortName(item.last_updated_name) || 'Somebody else';
}

/*
  What the live feed does to the rows on screen when somebody else saves a line.

  **A line you are typing into is left alone**, which is what the band at the top
  of a shared location promises. Before this, the other person saving the same
  line replaced the number in your box mid keystroke: halfway through typing 118
  and the box becomes their 40, with nothing to say why. Everything else about
  their row is taken, so a note or a flag they add still arrives; only the number
  under the cursor is kept.
*/
export function mergeLive(rows, incoming, focusedId) {
  return (rows || []).map(r => {
    if (!incoming || r.id !== incoming.id) return r;
    if (focusedId && focusedId === r.id) return { ...r, ...incoming, quantity: r.quantity };
    return { ...r, ...incoming };
  });
}
